import { Stack, Tooltip, Typography } from "@mui/material"
import { ReactElement, useContext } from "react"

import { ExistingInverterSelectionFields } from "./selectExistingInverter"
import { SUPPORT_REFER_ENTITY } from "../../../../../errors/errorMessages"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import AppButton from "../../../../buttons/appButton"
import AppAlert from "../../../../dataDisplay/alerts/appAlert"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import { AppCard, AppCardContent } from "../../../../surfaces/appCard"
import { CreateV2GBundleContext } from "../providers/baseProviders/createV2GBundleProvider"

const SelectGenerationPath = (): ReactElement => {
    const {
        currentIndex,
        setCurrentIndex,
        generationMode,
        setGenerationMode,
        selectedInverter,
        setSelectedInverter,
        setTtrFormData,
        inverterCategoryId,
        v2gDeviceTypeId,
        referenceDataLoading,
    } = useContext(CreateV2GBundleContext)

    const referenceDataMissing =
        !referenceDataLoading &&
        (inverterCategoryId === null || v2gDeviceTypeId === null)

    const onSelectExisting = (): void => {
        setGenerationMode("existing")
    }

    const onCreateNew = (): void => {
        if (
            referenceDataLoading ||
            inverterCategoryId === null ||
            v2gDeviceTypeId === null
        ) {
            return
        }

        setGenerationMode("new")
        setSelectedInverter(null)
        setTtrFormData((prev) => ({
            ...prev,
            coreDetails: {
                ...prev.coreDetails,
                deviceCategory: inverterCategoryId,
                deviceType: v2gDeviceTypeId,
            },
        }))
        setCurrentIndex(currentIndex + 1)
    }

    const onClickNext =
        generationMode === null
            ? undefined
            : (): void => {
                  setCurrentIndex(currentIndex + 1)
              }

    const nextDisabled =
        generationMode === null ||
        (generationMode === "existing" && !selectedInverter)

    const createNewDisabled = referenceDataLoading || referenceDataMissing
    let createNewTooltip: string | undefined
    if (referenceDataLoading) {
        createNewTooltip = "Loading reference data..."
    } else if (referenceDataMissing) {
        createNewTooltip = "V2G reference data unavailable"
    }

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 2}>
                <FormHeader
                    header="Inverter Options"
                    subHeader="Choose whether to link an existing inverter or create a new inverter as part of this V2G bundle"
                    index={currentIndex + 1}
                />
                <AppCardContent>
                    <Stack spacing={GRID_SPACING * 2}>
                        <Typography color="text.secondary">
                            A V2G bundle consists of one EV Charge Point (AC)
                            and one inverter.
                        </Typography>
                        {referenceDataMissing && (
                            <AppAlert severity="error">
                                {`V2G reference data is unavailable, so a new inverter cannot be created right now. Please contact ${SUPPORT_REFER_ENTITY}.`}
                            </AppAlert>
                        )}
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            spacing={GRID_SPACING}
                        >
                            <AppButton
                                variant={
                                    generationMode === "existing"
                                        ? "contained"
                                        : "outlined"
                                }
                                onClick={onSelectExisting}
                            >
                                Select Existing Inverter
                            </AppButton>
                            <Tooltip
                                title={createNewTooltip ?? ""}
                                disableHoverListener={!createNewTooltip}
                            >
                                <span>
                                    <AppButton
                                        variant={
                                            generationMode === "new"
                                                ? "contained"
                                                : "outlined"
                                        }
                                        onClick={onCreateNew}
                                        disabled={createNewDisabled}
                                    >
                                        Create New Inverter
                                    </AppButton>
                                </span>
                            </Tooltip>
                        </Stack>
                        {generationMode === "existing" && (
                            <ExistingInverterSelectionFields />
                        )}
                    </Stack>
                </AppCardContent>
                <FormNavigation
                    onClickBack={() => setCurrentIndex(currentIndex - 1)}
                    onClickNext={onClickNext}
                    nextDisabled={nextDisabled}
                />
            </Stack>
        </AppCard>
    )
}

export default SelectGenerationPath
